/**
 * @P120 — the last piece of the FE Health dashboard's production-grade precondition: *"poll retry/backoff,
 * concurrency, fail posture, worker monitoring"* (`project120_...md`, the FE row).
 *
 * WHAT WAS ACTUALLY MISSING (measured 2026-10-07, before writing this). The panel's fail posture was
 * already good — every catch leaves an honest note rather than an empty grid — and the `live` flags
 * already guard against a stale write. Two things were not:
 *
 *   1. **No timeout anywhere.** `apiCall` is a bare `fetch`, so a socket that never answers leaves
 *      `readinessLoading` true FOREVER: the panel spins with no end and no explanation. A hung read and
 *      a slow one were the same state to the user.
 *   2. **No retry.** A transient blip needed a human to close and reopen the drawer — the note literally
 *      told her to. That is a fail posture with a manual step in it.
 *
 * WHY THE RETRY IS BOUNDED BY ATTEMPTS, NOT BY A CLOCK. `RULES_CONTINUE`: *"a loop whose pacing is
 * injectable is bounded by attempts, not only by time — a test double that defeats the pacing must end the
 * loop, not spin it."* A retry loop hidden behind a `sleep` is exactly that shape, so `attempts` is the
 * bound and `sleep` is injectable: the check in `tests/warmup/check_resilient_read.mjs` runs the real
 * function with a no-op sleep and asserts the call COUNT.
 *
 * WHAT IS *NOT* RETRIED, and this is the important half. **A MUTATING CALL IS NEVER RETRIED**, because a
 * retried SEND is a second real email: `warmupTestHealth` is therefore wrapped in the timeout only, and
 * the timeout is derived from the wait she asked for, because cutting off a probe that was still running
 * would report a failure for mail that had already gone.
 *
 * ⚠️ AND WHAT THE RETRY ACTUALLY COVERS, corrected 2026-10-09 by the external challenge: this comment
 * used to say *"only a read that received NO ANSWER is retried"*, which the code below does not do — the
 * `catch` is total, so a read whose answer arrived but could not be PARSED is retried too. That is
 * defensible rather than accidental (a truncated or malformed body is often transient, and a READ is
 * idempotent, so the retry can only cost budget), but THE CLAIM WAS WRONG and this file's whole
 * justification is that its comments can be trusted. The accurate rule:
 *   · a refusal is NOT retried, because this codebase's convention is that the BE's own envelope carries
 *     the verdict (`status: false`) and a deliberate refusal arrives as a PARSED body, never as a throw;
 *   · a read that timed out, or whose body could not be parsed, IS retried, within `attempts`;
 *   · a mutating call is excluded from this wrapper entirely.
 */

/** No answer arrived inside the budget. Retryable for a READ; ambiguous for a send. */
export class ReadTimedOut extends Error {
  constructor(public readonly timeoutMs: number) {
    super(`no answer within ${timeoutMs}ms`);
    this.name = "ReadTimedOut";
  }
}

/** Every attempt was made and none got an answer. Carries the count so a caller can say how hard it tried. */
export class ReadsExhausted extends Error {
  constructor(
    public readonly attempts: number,
    public readonly lastError: unknown,
  ) {
    super(`${attempts} attempt(s), none answered: ${lastError instanceof Error ? lastError.message : String(lastError)}`);
    this.name = "ReadsExhausted";
  }
}

export interface ReadPolicy {
  /** Total attempts, INCLUDING the first — the bound the whole loop answers to. */
  attempts: number;
  /** Backoff base; the nth retry waits `baseDelayMs * 2^(n-1)`, capped by `maxDelayMs`. */
  baseDelayMs: number;
  maxDelayMs: number;
  /** Per-attempt budget. */
  timeoutMs: number;
  /** Injectable so a check can defeat the pacing instead of waiting for it. */
  sleep?: (ms: number) => Promise<void>;
}

/** Race `work` against its budget, and always clear the timer so nothing outlives the call.
 *
 * ⚠️ AND THE RACE IS NOT ENOUGH — THE WORK MUST BE ABORTABLE (F12 challenge `F5`, 2026-10-11).
 * `Promise.race` ends the CALLER's wait; it does nothing to the `fetch` inside. Measured before the
 * fix: the socket stayed open until the browser gave up, so every timed-out attempt left a connection
 * consuming one of the browser's ~6 per host — and under a slow network with a few drawer opens that
 * is how legitimate requests start queueing BEHIND abandoned ones. `withTimeout` now owns an
 * `AbortController`, hands its signal to `work`, and **aborts before it rejects**, so the socket is
 * released at the moment the caller stops waiting.
 *
 * THE SIGNAL IS PASSED, NEVER REQUIRED: `work` may ignore it (a non-fetch body, a test double), which
 * is what keeps this additive for every existing caller.
 */
export async function withTimeout<T>(
  work: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number,
): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      work(controller.signal),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          // Abort FIRST, then reject: the caller's `catch` runs after the socket is already released.
          controller.abort();
          reject(new ReadTimedOut(timeoutMs));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * A READ, retried on silence only. Throws `ReadsExhausted` once the attempt bound is spent, so the
 * caller's existing `catch` runs and the spinner ENDS — which is the defect this exists to close.
 *
 * Each attempt gets its OWN abortable budget (`withTimeout`), so a stalled attempt's socket is
 * released before the next one starts rather than accumulating behind the retry.
 */
export async function readWithRetry<T>(
  read: (signal: AbortSignal) => Promise<T>,
  policy: ReadPolicy,
): Promise<T> {
  const attempts = Math.max(1, Math.floor(policy.attempts));
  const sleep = policy.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  let lastError: unknown;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await withTimeout(read, policy.timeoutMs);
    } catch (err) {
      lastError = err;
      if (attempt === attempts) break;
      const delay = Math.min(policy.maxDelayMs, policy.baseDelayMs * 2 ** (attempt - 1));
      await sleep(delay);
    }
  }
  throw new ReadsExhausted(attempts, lastError);
}
